/**
 * StatusChip — Reusable tonal status chip component
 *
 * Maps submission statuses to Material Design 3 tonal color classes
 * per the Gem State Legacy design system.
 *
 * Requirements: 8.6, 9.6
 */

export interface StatusChipProps {
  status: 'APPROVED' | 'SUBMITTED' | 'PENDING' | 'REJECTED' | 'NEEDS_INFO' | 'DRAFT' | 'VERIFIED';
  /** Override the displayed text (defaults to a formatted version of the status) */
  label?: string;
}

const statusStyles: Record<StatusChipProps['status'], string> = {
  APPROVED: 'bg-success-muted text-success-text',
  VERIFIED: 'bg-success-muted text-success-text',
  SUBMITTED: 'bg-muted text-muted-foreground',
  PENDING: 'bg-muted text-muted-foreground',
  REJECTED: 'bg-destructive-muted text-destructive-text',
  NEEDS_INFO: 'bg-destructive-muted text-destructive-text',
  DRAFT: 'bg-muted text-muted-foreground',
};

const defaultLabels: Record<StatusChipProps['status'], string> = {
  APPROVED: 'Approved',
  VERIFIED: 'Verified',
  SUBMITTED: 'Submitted',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  NEEDS_INFO: 'Needs Info',
  DRAFT: 'Draft',
};

export function StatusChip({ status, label }: StatusChipProps) {
  return (
    <span
      data-testid="status-chip"
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusStyles[status]}`}
    >
      {label ?? defaultLabels[status]}
    </span>
  );
}
